import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import * as reportGeneration from '../../../../../redux/reportGeneration';
import { BUTTON_SIZE, BUTTON_TYPE, Icon, icons, NavigationButton } from '../../../../generic';
import { TooltipButton } from '../../../../generic/Button';
import type { ProjectContext, ReportCategory } from '../../shared/types';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption } from '../shared/types';
import { getDownloadURL } from '../projectLevel/utils';
import { useToggle } from '../../../../../hooks';
import EmailDialog from './EmailDialog';
import PublicLinkButton from './PublicLinkButton';
import Flex from '../../../../generic/Flex';
import { isFreeTier, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../../../../shared/subscriptions';
import { window } from '../../../../../globals';
import PaidFeatureGate from '../../../../generic/Gates/PaidFeatureGate';
import { ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS } from '../../../../../../shared/featureFlags/flagNames';
import type { Revision } from '../../../../../../shared/model-typings';
import { useIsFeatureEnabled } from '../../../../../helpers/features';
import { ReportCategories } from '../../ReportGeneration/utils';

// Number of dependencies above which a project's licensing report will
// use the "async email" workflow instead of allowing a direct download.
export const EMAIL_DEPENDENCY_THRESHOLD = 500;

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  revision?: Revision;
};

const ProjectActionButtons: VoidFunctionComponent<Props> = ({
  format,
  reportType,
  includeOptions,
  dependencyMetadataOptions,
  isDisabled,
  revision,
}: Props) => {
  const isLicensingReport = reportType === ReportCategories.Licensing;
  const isSbomReport = reportType === ReportCategories.SBOM;
  const isFixPlanReport = reportType === ReportCategories.FixPlan;

  const isPaidFeatureGateRestricted = isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const reportContext = useSelector(reportGeneration.selectors.getReportContext);
  const [isToggled, onToggle] = useToggle();

  const shouldAlwaysDownloadReports =
    useIsFeatureEnabled(ENABLED_DOWNLOADING_LARGE_PROJECT_LICENSING_REPORTS);

  // Use the revision's dep count to determine if the report should be emailed (instead of downloading).
  const dependencyCount = revision?.dependency_count ?? 0;
  const areDepsOverThreshold = dependencyCount > EMAIL_DEPENDENCY_THRESHOLD;
  // Other case is that Licensing PDF reports are always emailed
  const isLicensingReportPDF = isLicensingReport && (format === 'PDF');
  const shouldEmailReport = !isFixPlanReport && (isLicensingReportPDF
    || (areDepsOverThreshold && !shouldAlwaysDownloadReports));

  // Show the PublicLinkButton if showing download button for  licensing and sbom reports.
  const shouldShowPublicLinkButton = (isLicensingReport || isSbomReport) && !shouldEmailReport;

  return (
    <Flex gap="4px">
      {isToggled && (
      <EmailDialog
        onClose={onToggle}
        format={format}
        reportType={reportType}
        includeOptions={includeOptions}
        dependencyMetadataOptions={dependencyMetadataOptions}
        />
      )}
      {shouldShowPublicLinkButton && (
      <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}>
        <PublicLinkButton
          isDisabled={isDisabled}
          format={format}
          reportType={reportType}
          includeOptions={includeOptions}
          dependencyMetadataOptions={dependencyMetadataOptions}
          />
      </PaidFeatureGate>
      )}
      {shouldEmailReport ? (
        <TooltipButton
          id="button-email-report"
          tooltip="Due to the project's size, this report will be delivered via email when ready."
          onClick={onToggle}
          showTooltip={!isToggled}
          size={BUTTON_SIZE.LARGE}
          type={BUTTON_TYPE.SECONDARY}
          isDisabled={isDisabled}
        >
          <Icon icon={icons.envelopeRegular} /> Email
        </TooltipButton>
      ) : (
        <NavigationButton
          isDisabled={isDisabled}
          openInNewTab
          size={BUTTON_SIZE.LARGE}
          to={
            getDownloadURL({
              format,
              projectRevisionLocator: (reportContext as ProjectContext).revision,
              reportType,
              includeOptions,
              dependencyMetadataOptions,
            }) || ''
          }
        >
          <Icon icon={icons.downloadRegular} /> Download
        </NavigationButton>
      )}
    </Flex>);
};

export default ProjectActionButtons;
